// Adaptateurs entre la représentation backend (SQL anglais, taux décimaux,
// enums anglais) et la représentation attendue par le moteur de calcul (proto v9,
// libellés français, taux en pourcentage, enums français).
//
// Le moteur extrait reste verbatim. Toute traduction se fait ICI, à la frontière.
import { legalNormalVatRate } from '../../../utils/vatLegalRates';

const VAT_PERIODICITY_TO_FR = { monthly: 'mensuelle', quarterly: 'trimestrielle', semiannual: 'semestrielle', annual: 'annuelle' };
const VAT_PERIODICITY_TO_EN = { mensuelle: 'monthly', trimestrielle: 'quarterly', semestrielle: 'semiannual', annuelle: 'annual' };

const DRIVER_TYPE_TO_FR = { pct_revenue: 'pct_ca_ligne', manual: 'manuel' };
const DRIVER_TYPE_TO_EN = { pct_ca_ligne: 'pct_revenue', manuel: 'manual' };

const CHANGE_TYPE_TO_FR = { hire: 'embauche', leave: 'depart', raise: 'augmentation' };

// Helpers
const pct = (decimal) => (Number(decimal) || 0) * 100;   // 0.081 → 8.1
const dec = (pctVal) => (Number(pctVal) || 0) / 100;     // 8.1 → 0.081

// ── Scenario : backend → moteur (FR) ────────────────────────────────────────

export function scenarioApiToEngine(apiScenario) {
  return {
    id: apiScenario.id,
    nom: apiScenario.name,
    type: apiScenario.type,
    horizon: apiScenario.horizon,
    moisDebut: apiScenario.start_month,
    anneeDebut: apiScenario.start_year,
    locked: apiScenario.locked,

    soldeInitial: Number(apiScenario.initial_balance) || 0,
    canton: apiScenario.canton,

    // TVA : régime au niveau tenant (voir tenantVatToEngine), injecté par
    // composeEngineScenario. Plus porté par le scénario depuis 2026-06-17.

    // Personnel
    salaireBrutMensuel: Number(apiScenario.gross_monthly_salary) || 0,
    nbMoisSalaire: apiScenario.salary_months,
    fteTotal: Number(apiScenario.total_fte) || 0,
    impotSourceMensuel: Number(apiScenario.monthly_withholding_tax) || 0,
    masseDeclareeAssurances: Number(apiScenario.declared_insurance_base) || 0,

    // Postes ouverts : pilotés par le bilan (résolus par poste/compte hors du
    // scénario, voir composeEngineScenario → poCreancesLines / poDettesLines).

    // Les sous-collections (revenus, variables, mensuel, etc.) sont remplies
    // séparément par les autres adapters via composeEngineScenario().
    revenus: [],
    variables: [],
    mensuel: [],
    specifique: [],
    primesBonus: [],
    jetons: [],
    variations: [],
    tauxCharges: [],
    assurancesPersonnes: [],

    // Champs non encore exposés côté UI Phase 1 — défauts neutres pour le moteur.
    fraisForfaitaires: 0,
    indemnites: 0,
  };
}

// ── Scenario : moteur (FR) ou form local → backend (EN) ─────────────────────

// Départ du plan ancré sur le dernier mois clôturé du tenant : le plan démarre
// le mois SUIVANT (l'arrêté du bilan d'ouverture = le dernier mois clôturé).
// `lastClosed` = date "YYYY-MM-DD" (tenant.last_closed_period) ; null si non
// défini → pas d'ancrage (on ne fabrique pas de valeur par défaut silencieuse).
export function startFromLastClosed(lastClosed) {
  if (!lastClosed) return null;
  const d = new Date(lastClosed);
  if (Number.isNaN(d.getTime())) return null;
  let start_month = d.getUTCMonth() + 1;   // 0-indexé, mois suivant le clôturé
  let start_year = d.getUTCFullYear();
  if (start_month > 11) { start_month = 0; start_year += 1; }
  return { start_month, start_year };
}

export function scenarioToApiCreate({
  name, type = 'base',
  start_month = 0,
  start_year = new Date().getFullYear(),
  ...rest
} = {}) {
  return {
    name: name || 'Nouveau scénario',
    type,
    horizon: 12,
    start_month,
    start_year,
    initial_balance: rest.initial_balance ?? 0,
    canton: rest.canton ?? null,
    // Devise + régime TVA vivent désormais sur le tenant (plus sur le scénario).
    salary_months: 13,
    gross_monthly_salary: 0,
    total_fte: 0,
    monthly_withholding_tax: 0,
    declared_insurance_base: 0,
  };
}

// ── Revenue line ────────────────────────────────────────────────────────────

export function revenueApiToEngine(apiRow) {
  return {
    id: apiRow.id,
    libelle: apiRow.label,
    detail: apiRow.detail || '',
    valeurs: (apiRow.monthly_values || []).map(Number),
    delai: apiRow.payment_lag,
    soumisTva: apiRow.vat_applicable,
    ordre: apiRow.display_order,
    // Part marchandise (0..1) : base des coûts d'achat = part marchandise du CA.
    goodsPct: Number(apiRow.goods_pct) || 0,
  };
}

export function revenueEngineToApi(engineRow, scenarioId) {
  return {
    scenario_id: scenarioId,
    label: engineRow.libelle,
    detail: engineRow.detail || null,
    monthly_values: engineRow.valeurs,
    payment_lag: engineRow.delai,
    vat_applicable: !!engineRow.soumisTva,
    display_order: engineRow.ordre || 0,
  };
}

// ── Variable expense (Section A %CA + Section B manuel) ─────────────────────

export function variableApiToEngine(apiRow) {
  return {
    id: apiRow.id,
    libelle: apiRow.label,
    driverType: DRIVER_TYPE_TO_FR[apiRow.driver_type] || 'manuel',
    ligneRevenuId: apiRow.revenue_ref_id || null,
    taux: pct(apiRow.pct_rate),                       // 0.10 → 10
    valeurs: (apiRow.monthly_values || []).map(Number),
    delai: apiRow.payment_lag,
    soumisTva: apiRow.vat_recoverable,
    ordre: apiRow.display_order,
  };
}

export function variableEngineToApi(engineRow, scenarioId) {
  const driver_type = DRIVER_TYPE_TO_EN[engineRow.driverType] || 'manual';
  return {
    scenario_id: scenarioId,
    label: engineRow.libelle,
    driver_type,
    revenue_ref_id: driver_type === 'pct_revenue' ? engineRow.ligneRevenuId : null,
    pct_rate: driver_type === 'pct_revenue' ? dec(engineRow.taux) : null,
    monthly_values: driver_type === 'manual' ? (engineRow.valeurs || Array(12).fill(0)) : null,
    payment_lag: engineRow.delai || 0,
    vat_recoverable: engineRow.soumisTva !== false,
    display_order: engineRow.ordre || 0,
  };
}

// ── Recurring expense (frais indirects récurrents) ─────────────────────────

export function recurringApiToEngine(apiRow) {
  return {
    id: apiRow.id,
    libelle: apiRow.label,
    montant: Number(apiRow.amount) || 0,
    mode: apiRow.mode,                           // 'monthly' | 'annual'  ↔  'mensuel' | 'annuel'
    moisActifs: apiRow.active_months || [],
    soumisTva: apiRow.vat_recoverable,
    ordre: apiRow.display_order,
  };
}
// Note : le moteur regarde charge.mode === 'annuel' (FR) — on traduit.
export function recurringEngineToApi(engineRow, scenarioId) {
  const mode = engineRow.mode === 'annuel' ? 'annual' : 'monthly';
  return {
    scenario_id: scenarioId,
    label: engineRow.libelle,
    amount: Number(engineRow.montant) || 0,
    mode,
    active_months: engineRow.moisActifs || [],
    vat_recoverable: engineRow.soumisTva !== false,
    display_order: engineRow.ordre || 0,
  };
}

// Pour le moteur (proto) : computeMensuelValeurs lit charge.mode avec valeur 'annuel' française.
// On expose donc une variante "engine-ready" qui traduit le mode EN→FR à la lecture.
export function recurringApiToEngineMode(apiRow) {
  const r = recurringApiToEngine(apiRow);
  return { ...r, mode: r.mode === 'annual' ? 'annuel' : 'mensuel' };
}

// ── One-off expense (frais ponctuels) ──────────────────────────────────────

export function oneOffApiToEngine(apiRow) {
  return {
    id: apiRow.id,
    libelle: apiRow.label,
    montant: Number(apiRow.amount) || 0,
    mois: apiRow.month,                          // 0-11
    soumisTva: apiRow.vat_recoverable,
  };
}
// Conversion vers le format `specifique` du proto (qui veut un tableau moisActifs + mode).
// Un one-off = mode mensuel sur 1 seul mois.
export function oneOffApiToEngineSpec(apiRow) {
  return {
    id: apiRow.id,
    libelle: apiRow.label,
    montant: Number(apiRow.amount) || 0,
    mode: 'mensuel',
    moisActifs: [apiRow.month],
    soumisTva: apiRow.vat_recoverable,
    delai: 0,
  };
}
export function oneOffEngineToApi(engineRow, scenarioId) {
  return {
    scenario_id: scenarioId,
    label: engineRow.libelle,
    amount: Number(engineRow.montant) || 0,
    month: engineRow.mois,
    vat_recoverable: engineRow.soumisTva !== false,
  };
}

// ── Payroll change (variations : embauche/départ/augmentation) ─────────────

export function payrollChangeApiToEngine(apiRow) {
  // Le proto v9 utilise `variations[]` avec { mois, montantMensuel }
  // mois = index horizon (typiquement = mois calendaire si start_month=0)
  return {
    id: apiRow.id,
    type: CHANGE_TYPE_TO_FR[apiRow.change_type] || apiRow.change_type,
    montantMensuel: Number(apiRow.amount) || 0,
    mois: apiRow.start_month,
  };
}
export function payrollChangeEngineToApi(engineRow, scenarioId) {
  const reverse = { embauche: 'hire', depart: 'leave', augmentation: 'raise' };
  return {
    scenario_id: scenarioId,
    change_type: reverse[engineRow.type] || engineRow.type,
    amount: Number(engineRow.montantMensuel) || 0,
    start_month: engineRow.mois,
  };
}

// ── Bonus (primes/jetons) ──────────────────────────────────────────────────
// Backend : une table treasury_bonuses avec kind='bonus' OU 'director_fee'.
// Proto :   primesBonus[] (kind='bonus') ET jetons[] (kind='director_fee'), séparés.

export function bonusApiToEngine(apiRow) {
  return {
    id: apiRow.id,
    montant: Number(apiRow.amount) || 0,
    mois: apiRow.month,
    kind: apiRow.kind,
  };
}
export function bonusEngineToApi(engineRow, scenarioId) {
  return {
    scenario_id: scenarioId,
    amount: Number(engineRow.montant) || 0,
    month: engineRow.mois,
    kind: engineRow.kind || 'bonus',
  };
}

// ── Social rate (cotisations cantonales : AVS, AC, AF, etc.) ───────────────

export function socialRateApiToEngine(apiRow) {
  // Le proto attend tauxCharges[] avec { code, libelle, employeur, employe, plafond }
  // Backend stocke en décimal — proto attend en pourcentage.
  return {
    id: apiRow.id,
    code: apiRow.code,
    libelle: apiRow.label,
    employeur: pct(apiRow.employer_rate),
    employe: pct(apiRow.employee_rate),
    plafond: apiRow.ceiling !== null ? Number(apiRow.ceiling) : null,
    ordre: apiRow.display_order,
  };
}
export function socialRateEngineToApi(engineRow, scenarioId) {
  return {
    scenario_id: scenarioId,
    code: engineRow.code,
    label: engineRow.libelle,
    employer_rate: dec(engineRow.employeur),
    employee_rate: dec(engineRow.employe),
    ceiling: engineRow.plafond !== null && engineRow.plafond !== undefined ? Number(engineRow.plafond) : null,
    display_order: engineRow.ordre || 0,
  };
}

// ── Insurance (LPP, LAA, IJM, LAAC, autres) ────────────────────────────────

export function insuranceApiToEngine(apiRow) {
  // Le proto attend assurancesPersonnes[] avec :
  // { code, libelle, tauxEstimatif (%), partEmployeur (%), partEmploye (%), moisPaiements[] }
  return {
    id: apiRow.id,
    code: apiRow.code,
    libelle: apiRow.label,
    tauxEstimatif: pct(apiRow.estimated_rate),
    partEmployeur: pct(apiRow.employer_share),
    partEmploye: pct(apiRow.employee_share),
    moisPaiements: apiRow.payment_months || [],
    ordre: apiRow.display_order,
  };
}
export function insuranceEngineToApi(engineRow, scenarioId) {
  return {
    scenario_id: scenarioId,
    code: engineRow.code,
    label: engineRow.libelle,
    estimated_rate: dec(engineRow.tauxEstimatif),
    employer_share: dec(engineRow.partEmployeur),
    employee_share: dec(engineRow.partEmploye),
    payment_months: engineRow.moisPaiements || [],
    display_order: engineRow.ordre || 0,
  };
}

// ── TVA : régime tenant → champs moteur (FR, taux en %) ────────────────────
// Le régime TVA est un fait légal de l'entreprise (tenant), partagé par tous
// les scénarios. Backend en décimal → moteur en %.
// `tauxTva` / `tauxTvaTdfn` (scalaires, legacy) restent le repli pour les
// fixtures/tests ; en production le moteur utilise `tauxTvaPctByMonth` /
// `tauxTvaTdfnPctByMonth` (year-aware, voir buildVatMonthRates).
export function tenantVatToEngine(tenant) {
  const t = tenant || {};
  return {
    methodeTva: t.vat_method || 'effective',
    baseTva: t.vat_basis === 'received' ? 'recues' : 'convenues',
    tauxTva: pct(t.vat_rate),
    tauxTvaTdfn: pct(t.vat_rate_tdfn),
    periodiciteTva: VAT_PERIODICITY_TO_FR[t.vat_periodicity] || 'trimestrielle',
    decalagePaiementTva: t.vat_payment_lag ?? 2,
  };
}

/**
 * Taux de TVA par mois de l'horizon (en %), résolu depuis la table année→taux
 * avec report du dernier taux connu (même règle que le backend). Le mois m de
 * l'horizon tombe sur l'année calendaire `anneeDebut + ⌊(moisDebut+m)/12⌋` ;
 * un prévisionnel 12 mois franchissant le 1er janvier applique donc le taux de
 * n+1 aux mois de n+1.
 *
 * @param {Array<{year:number,vat_rate:?number,vat_rate_tdfn:?number}>} vatRates
 * @param {'effective'|'tdfn'} method
 * @returns {number[]|null} tableau de % (longueur horizon) ou null si aucun taux.
 */
export function buildVatMonthRates(vatRates, anneeDebut, moisDebut, horizon, method) {
  const rows = (vatRates || []).filter(r => Number.isInteger(r.year)).slice().sort((a, b) => a.year - b.year);
  const pick = (r) => (method === 'tdfn' ? r.vat_rate_tdfn : r.vat_rate);
  const known = rows.filter(r => pick(r) != null);
  // Effectif : le taux légal suisse s'applique par défaut (barème AFC) ; les
  // lignes saisies ne sont que des OVERRIDES (ex. hébergement 3,8 %). TDFN : pas
  // de barème légal → il faut un taux saisi (sinon null, VAT non calculée).
  const legal = method === 'tdfn' ? null : (y) => legalNormalVatRate(y);
  if (!known.length && !legal) return null;
  const resolve = (year) => {
    let val = null;
    for (const r of known) { if (r.year <= year) val = Number(pick(r)); }
    if (val != null) return val;
    if (legal) return legal(year);              // effectif → taux légal de l'année
    return known.length ? Number(pick(known[0])) : null;  // tdfn → plus ancien connu
  };
  const H = horizon || 12;
  const start = Number.isInteger(moisDebut) ? moisDebut : 0;
  return Array.from({ length: H }, (_, m) => {
    const y = anneeDebut + Math.floor((start + m) / 12);
    const v = resolve(y);
    return v == null ? null : v * 100;  // décimal → %
  });
}

// ── Assemblage final d'un scénario "engine-ready" ──────────────────────────

export function composeEngineScenario(apiScenario, {
  revenues = [],
  variables = [],
  recurring = [],
  oneOff = [],
  payrollChanges = [],
  bonuses = [],
  socialRates = [],
  insurances = [],
  openingReceivables = [],
  openingPayables = [],
  initialBalance = null,
  vatRates = [],
} = {}, tenant = null) {
  const base = scenarioApiToEngine(apiScenario);
  // Solde initial : si des comptes Liquidités du bilan sont inclus, leur somme
  // (lien vivant) prime sur le champ scénario ; sinon on garde le solde scénario.
  if (initialBalance !== null && initialBalance !== undefined) {
    base.soldeInitial = Number(initialBalance) || 0;
  }
  // Split bonuses → primesBonus / jetons (proto v9 les sépare)
  const primesBonus = bonuses.filter(b => b.kind === 'bonus').map(bonusApiToEngine);
  const jetons = bonuses.filter(b => b.kind === 'director_fee').map(bonusApiToEngine);
  const tauxTvaPctByMonth = buildVatMonthRates(vatRates, base.anneeDebut, base.moisDebut, base.horizon, 'effective');
  const tauxTvaTdfnPctByMonth = buildVatMonthRates(vatRates, base.anneeDebut, base.moisDebut, base.horizon, 'tdfn');
  return {
    ...base,
    ...tenantVatToEngine(tenant),
    tauxTvaPctByMonth,
    tauxTvaTdfnPctByMonth,
    revenus: revenues.map(revenueApiToEngine),
    // affects_cash=false → hypothèse de marge non décaissée : exclue du cash.
    variables: variables.filter(v => v.affects_cash !== false).map(variableApiToEngine),
    mensuel: recurring.map(recurringApiToEngineMode),
    specifique: oneOff.map(oneOffApiToEngineSpec),
    variations: payrollChanges.map(payrollChangeApiToEngine),
    primesBonus,
    jetons,
    tauxCharges: socialRates.map(socialRateApiToEngine),
    assurancesPersonnes: insurances.map(insuranceApiToEngine),
    // Opening items (multi-customer / multi-supplier). Each line carries an
    // amount and a calendar-fixed 12-element % profile.
    poCreancesLines: (openingReceivables || []).map((r) => ({
      id: r.id,
      name: r.customer_name,
      amount: Number(r.amount) || 0,
      collectionPct: r.collection_pct || Array(12).fill(0),
    })),
    poDettesLines: (openingPayables || []).map((p) => ({
      id: p.id,
      name: p.supplier_name,
      amount: Number(p.amount) || 0,
      paymentPct: p.payment_pct || Array(12).fill(0),
    })),
  };
}

// ── Helpers UI (formatage commun) ───────────────────────────────────────────

// Format CHF strict : apostrophe suisse pour les milliers, point pour décimales.
// Solution propre qui contourne le bug pré-existant de KpiCard (transforme aussi
// les décimales en apostrophes → "143'773'00" au lieu de "143'773.00").
export function formatCHF(n, opts = {}) {
  const { decimals = 0, currency = 'CHF' } = opts;
  if (n === null || n === undefined || isNaN(n)) return `— ${currency}`;
  if (Math.abs(n) < 0.5 && decimals === 0) return `0 ${currency}`;
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const rounded = decimals > 0 ? abs.toFixed(decimals) : String(Math.round(abs));
  const [intPart, decPart] = rounded.split('.');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${sign}${withSep}${decPart ? '.' + decPart : ''} ${currency}`;
}
